import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Modal, Space, Typography, message } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import { servicesAPI } from '../../api/client';
import { downloadBlobResponse } from '../../utils/downloadBlob';

const { Text } = Typography;

export default function ServiceExportModal({ open, onClose, service, accessSummary }) {
  const [includeInfo, setIncludeInfo] = useState(true);
  const [includePurchases, setIncludePurchases] = useState(true);
  const [includePurchasedUsers, setIncludePurchasedUsers] = useState(true);
  const [includeGreyUsers, setIncludeGreyUsers] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIncludeInfo(true);
    setIncludePurchases(true);
    setIncludePurchasedUsers(true);
    setIncludeGreyUsers(true);
  }, [open]);

  const includeUsers = includePurchasedUsers || includeGreyUsers;
  const hasAnySection = includeInfo || includeUsers || includePurchases;
  const usersIndeterminate = includePurchasedUsers !== includeGreyUsers;

  const usersLabel = useMemo(() => {
    const total = Number(accessSummary?.totalUsers || 0);
    return total ? `Пользователи (${total})` : 'Пользователи';
  }, [accessSummary?.totalUsers]);

  const handleUsersToggle = (checked) => {
    setIncludePurchasedUsers(checked);
    setIncludeGreyUsers(checked);
  };

  const handleExport = async () => {
    if (!service?.id || !hasAnySection) return;

    setLoading(true);
    try {
      const response = await servicesAPI.exportDetail(service.id, {
        include_info: includeInfo,
        include_users: includeUsers,
        include_purchases: includePurchases,
        include_purchased_users: includePurchasedUsers,
        include_grey_users: includeGreyUsers,
      });
      downloadBlobResponse(response, 'service-report.xlsx');
      onClose();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Не удалось скачать отчёт');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={(
        <Space>
          <FileExcelOutlined />
          <span>Экспорт сервиса «{service?.name || ''}»</span>
        </Space>
      )}
      onOk={handleExport}
      onCancel={onClose}
      okText="Скачать Excel"
      cancelText="Отмена"
      confirmLoading={loading}
      okButtonProps={{ disabled: !hasAnySection }}
      width={520}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Каждый выбранный раздел попадёт на отдельный лист Excel.
      </Text>

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Checkbox
          checked={includeInfo}
          onChange={(event) => setIncludeInfo(event.target.checked)}
        >
          Информация о сервисе
        </Checkbox>

        <div>
          <Checkbox
            checked={includeUsers}
            indeterminate={usersIndeterminate}
            onChange={(event) => handleUsersToggle(event.target.checked)}
          >
            {usersLabel}
          </Checkbox>
          <Space direction="vertical" size={6} style={{ display: 'flex', marginTop: 8, marginLeft: 24 }}>
            <Checkbox
              checked={includePurchasedUsers}
              onChange={(event) => setIncludePurchasedUsers(event.target.checked)}
            >
              Купленные лицензии ({Number(accessSummary?.purchasedUsers || 0)})
            </Checkbox>
            <Checkbox
              checked={includeGreyUsers}
              onChange={(event) => setIncludeGreyUsers(event.target.checked)}
            >
              Грязный домен ({Number(accessSummary?.greyUsers || 0)})
            </Checkbox>
          </Space>
        </div>

        <Checkbox
          checked={includePurchases}
          onChange={(event) => setIncludePurchases(event.target.checked)}
        >
          История закупок
        </Checkbox>
      </Space>
    </Modal>
  );
}
